import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Wand2, ArrowRight } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KEEP = "__keep__";
const key = (r) => `${r.category}||${r.department}`;

const FixRow = ({ r, choice, groups, onChange, onApply, busy }) => {
  const groupList = Object.entries(r.product_groups).sort((a, b) => b[1] - a[1]).map(([g, n]) => `${g || "Unassigned"} ${n}`).join(", ");
  return (
    <tr className="border-b align-top" data-testid={`fixer-row-${r.category}-${r.department}`}>
      <td className="p-2 text-xs text-slate-500">{r.category || "—"}</td>
      <td className="p-2">
        <p className="font-semibold">{r.department} {r.legacy && <Badge className="ml-1 bg-amber-100 text-amber-800">legacy</Badge>}</p>
        <p className="text-xs text-slate-500">{r.count} products · {groupList}</p>
      </td>
      <td className="p-2"><ArrowRight className="mt-2 h-4 w-4 text-slate-400" /></td>
      <td className="p-2">
        <select className="w-44 rounded border px-2 py-1.5 text-sm" value={choice.to} onChange={(e) => onChange({ ...choice, to: e.target.value })} data-testid={`fixer-to-${r.category}-${r.department}`}>
          <option value="">{r.legacy ? "— pick department —" : `Keep ${r.department}`}</option>
          {r.options.filter((o) => o !== r.department).map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      </td>
      <td className="p-2">
        <select className="w-44 rounded border px-2 py-1.5 text-sm" value={choice.group} onChange={(e) => onChange({ ...choice, group: e.target.value })} data-testid={`fixer-group-${r.category}-${r.department}`}>
          <option value={KEEP}>Keep product groups</option>
          {groups.map((g) => <option key={g} value={g}>{g}</option>)}
          <option value="">Unassigned</option>
        </select>
      </td>
      <td className="p-2">
        <Button size="sm" disabled={busy || (!choice.to && choice.group === KEEP)} onClick={onApply} data-testid={`fixer-apply-${r.category}-${r.department}`}>{choice.to ? "Move" : "Update"} {r.count}</Button>
      </td>
    </tr>
  );
};

export const DepartmentFixerModal = ({ open, onClose, onComplete }) => {
  const [data, setData] = useState(null);
  const [groups, setGroups] = useState([]);
  const [choices, setChoices] = useState({});
  const [showAll, setShowAll] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setData(null);
    const [{ data: d }, { data: g }] = await Promise.all([axios.get(`${API}/products/department-fixer`), axios.get(`${API}/products/groups`)]);
    setGroups(g.groups || []);
    setData(d);
    setChoices(Object.fromEntries(d.rows.map((r) => [key(r), {
      to: r.legacy ? r.suggested_department || "" : "",
      group: r.legacy && r.suggested_group ? r.suggested_group : KEEP,
    }])));
  }, []);

  useEffect(() => { if (open) load().catch(() => toast.error("Could not load departments (admin only)")); }, [open, load]);

  const rows = useMemo(() => (data?.rows || []).filter((r) => showAll || r.legacy), [data, showAll]);

  const apply = async (list) => {
    const items = list.map((r) => {
      const c = choices[key(r)];
      return { category: r.category, from_department: r.department, to_department: c.to || null, product_group: c.group === KEEP ? null : c.group };
    }).filter((i) => i.to_department || i.product_group !== null);
    if (!items.length) return toast.error("Pick a department or product group first");
    const total = list.reduce((s, r) => s + r.count, 0);
    if (!window.confirm(`Update ${total} products in ${items.length} department(s)?`)) return;
    setBusy(true);
    try {
      const { data: res } = await axios.post(`${API}/products/department-fixer/apply`, { items });
      toast.success(`Updated ${res.products_updated} products${res.departments_removed.length ? ` · removed ${res.departments_removed.length} empty legacy department(s)` : ""}`);
      await load();
      onComplete?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update");
    } finally {
      setBusy(false);
    }
  };

  const ready = rows.filter((r) => choices[key(r)]?.to || choices[key(r)]?.group !== KEEP);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl" data-testid="department-fixer-modal">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Wand2 className="h-5 w-5 text-amber-600" /> Fix Departments & Product Groups</DialogTitle></DialogHeader>
        {!data ? <Loader2 className="mx-auto my-10 h-8 w-8 animate-spin" /> : (
          <>
            <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
              <p data-testid="fixer-summary"><b>{data.legacy_count}</b> legacy departments on <b>{data.legacy_products}</b> products. Suggestions are pre-filled — check them, then move.</p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setShowAll((v) => !v)} data-testid="fixer-toggle-all">{showAll ? "Only legacy" : "Show all departments"}</Button>
                <Button size="sm" className="bg-amber-600 hover:bg-amber-700" disabled={busy || !ready.length} onClick={() => apply(ready)} data-testid="fixer-apply-all">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : `Apply all (${ready.length})`}
                </Button>
              </div>
            </div>
            <div className="max-h-[60vh] overflow-auto rounded border">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-slate-100 text-left text-xs uppercase text-slate-600">
                  <tr><th className="p-2">Category</th><th className="p-2">Current department</th><th /><th className="p-2">Move to</th><th className="p-2">Product group</th><th /></tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <FixRow key={key(r)} r={r} groups={groups} busy={busy} choice={choices[key(r)] || { to: "", group: KEEP }}
                      onChange={(c) => setChoices({ ...choices, [key(r)]: c })} onApply={() => apply([r])} />
                  ))}
                  {rows.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-slate-500" data-testid="fixer-empty">No legacy departments left</td></tr>}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-slate-500">Missing a department in the list? Add it under its category in Settings first. Empty legacy departments are tidied away automatically.</p>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
